import { MouseEvent, useState } from "react"
import { ButtonBase, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"

import { STATUS_MAP } from "../../constants"
import { FilteredStatuses, MeetingServiceOrderStatus } from "../../meetingServiceTypes"
import { ProductionProgressBar } from "./ProductionProgressBar"

/** Delivery statuses the summary pill can track. */
export const SUMMARY_STATUSES: ReadonlyArray<FilteredStatuses> = [
    MeetingServiceOrderStatus.APPROVED,
    MeetingServiceOrderStatus.PREPARING,
    MeetingServiceOrderStatus.READY,
    MeetingServiceOrderStatus.DELIVERED,
]

type StyleProps = Readonly<{
    menuWidth: number | undefined
}>

const useStyles = makeStyles<StyleProps>()((theme, { menuWidth }) => ({
    root: {
        gap: "1rem",
        padding: ".875rem 1rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        background: theme.palette.background.paper,
    },
    caret: {
        flexShrink: 0,
        fontSize: ".75rem",
        color: theme.palette.tailwind.slate[400],
    },
    label: {
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
    count: {
        minWidth: "2rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
        // When the pill stretches to a full-width row on narrow screens, the count hugs the right edge.
        [theme.breakpoints.down("md")]: {
            marginLeft: "auto",
        },
    },
    menuItem: {
        padding: ".5rem 1rem",
    },
    // The menu spans the full width of the pill it drops down from (the anchor is measured in px).
    menuPaper: {
        width: menuWidth !== undefined ? `${menuWidth / 16}rem` : undefined,
    },
}))

type Props = Readonly<{
    status: FilteredStatuses
    onStatusChange: (status: FilteredStatuses) => void
    ready: number
    total: number
    className?: string
}>

export function ProductionStatusSummary({ status, onStatusChange, ready, total, className }: Props) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const { classes, cx } = useStyles({ menuWidth: anchorEl?.offsetWidth })

    const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const handleSelect = (selected: FilteredStatuses) => {
        onStatusChange(selected)
        handleClose()
    }

    const { statusItemLabel, stateTranslationId } = STATUS_MAP[status]

    return (
        <>
            <Stack
                component={ButtonBase}
                onClick={handleOpen}
                direction="row"
                alignItems="center"
                className={cx(classes.root, className)}
            >
                <LocalizedStrict id={stateTranslationId}>
                    <Typography variant="body2" component="span" className={classes.label}>
                        {statusItemLabel}
                    </Typography>
                </LocalizedStrict>
                <ProductionProgressBar ready={ready} total={total} />
                <Typography variant="body2" component="span" className={classes.count}>
                    {ready}/{total}
                </Typography>
                <FontAwesomeIcon icon={faChevronDown} className={classes.caret} />
            </Stack>
            <Menu
                disableScrollLock
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ className: classes.menuPaper }}
            >
                {SUMMARY_STATUSES.map((summaryStatus) => (
                    <MenuItem
                        key={summaryStatus}
                        selected={summaryStatus === status}
                        className={classes.menuItem}
                        onClick={() => handleSelect(summaryStatus)}
                    >
                        <LocalizedStrict id={STATUS_MAP[summaryStatus].stateTranslationId}>
                            <Typography variant="body2" component="span" className={classes.label}>
                                {STATUS_MAP[summaryStatus].statusItemLabel}
                            </Typography>
                        </LocalizedStrict>
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}
